<template>
  <component v-bind="binds" :is="buttonType" ref="btn" :class="classes" @click="onClick">
    <template v-if="!isLoading">
      {{ text }}
      <i v-if="icon" :class="{ ['icon']: true, [`${icon}`]: !!icon }" aria-hidden="true"></i>
    </template>
    <div v-else class="btn__loading" :aria-label="loadingLabel">
      <span aria-hidden="true"></span>
      <span aria-hidden="true"></span>
      <span aria-hidden="true"></span>
    </div>
    <span
      v-if="circle.isVisible"
      class="btn__circle"
      :style="{ left: `${circle.posX}px`, top: `${circle.posY}px` }"
      aria-hidden="true"
      @animationend="resetAnimation"
    ></span>
  </component>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted } from 'vue';
import { getLinkTag } from '../../../utils/linkTransformer';
import { useAppStore } from '../../../stores/appStore';
import type { LinkValue, CircleState } from './types';

const ANCHOR = 'anchor';
const GUESTLINE = 'guestline';

const emit = defineEmits<{
  click: [];
}>();

const props = withDefaults(
  defineProps<{
    label?: string;
    type?: string;
    variant?: 'primary' | 'secondary';
    disabled?: boolean;
    isLoading?: boolean;
    loadingLabel?: string;
    link?: LinkValue | null;
    icon?: string;
    rounded?: boolean;
    limit?: number;
    tabindex?: number | null;
  }>(),
  {
    type: 'button',
    variant: 'primary',
    disabled: false,
    isLoading: false,
    loadingLabel: 'Loading...',
    link: null,
    rounded: false,
    limit: 160,
    tabindex: null,
  }
);

const isMounted = ref(false);
const btn = ref<HTMLElement | null>(null);

const isGuestlineButton = computed(() => props.link?.linktype === GUESTLINE);

const appStore = isGuestlineButton.value ? useAppStore() : null;

const href = computed(() => {
  let url = props.link?.href;
  if (url && props.link?.anchor && props.link?.linktype !== ANCHOR) url += `#${props.link.anchor}`;
  return url;
});

const buttonType = computed(() => {
  if (props.type === 'link' && !isGuestlineButton.value) {
    // return router-link for internal links and just a for external
    return getLinkTag(props.link?.linktype, href.value);
  } else return 'button';
});

const classes = computed(() => ({
  ['btn']: true,
  ['no-transition']: !isMounted.value,
  [`btn--${props.variant}`]: !!props.variant,
  ['btn--loading']: props.isLoading,
  ['btn--icon']: !!props.icon && !props.isLoading,
  ['btn--rounded']: props.rounded,
  ['btn--only-icon']: !!props.icon && !props.label && !props.link?.text,
}));

const text = computed(() => {
  const txt = props.label || props.link?.text;
  if (!txt?.length || !props.limit) return txt;
  return txt.length > props.limit ? txt.substring(0, props.limit) + '...' : txt;
});

const binds = computed(() => {
  const obj: Record<string, string | boolean | number | null | undefined> = {};

  if (props.type !== 'link') obj.type = props.type;
  if (buttonType.value === 'router-link' && href.value) obj.to = href.value;
  if (buttonType.value === 'a' && href.value) obj.href = href.value;
  if (props.link?.target) obj.target = props.link.target;
  if (props.link?.target === '_blank') obj.rel = 'noopener noreferrer';
  if (props.link?.alt) obj.alt = props.link.alt;
  if (props.disabled) obj.disabled = props.disabled;
  obj.tabindex = props.tabindex;

  return obj;
});

const circle = reactive<CircleState>({
  isVisible: false,
  posX: null,
  posY: null,
});

const resetAnimation = () => {
  circle.isVisible = false;
  circle.posX = null;
  circle.posY = null;
};

const onClick = (e: MouseEvent) => {
  if (props.isLoading) return;
  if (isGuestlineButton.value && props.link?.href) {
    appStore?.setGuestlineActiveUrl(props.link.href);
  }

  emit('click');
  resetAnimation();

  const el = btn.value as HTMLElement & { $el?: HTMLElement };
  const dims = el?.getBoundingClientRect
    ? el.getBoundingClientRect()
    : el?.$el?.getBoundingClientRect();

  if (!dims) return;

  const btnArea = {
    minX: dims.x,
    maxX: dims.width,
    minY: dims.y,
    maxY: dims.height,
  };

  const mousePos = {
    x: e.clientX,
    y: e.clientY,
  };

  const x = mousePos.x - btnArea.minX;
  const y = mousePos.y - btnArea.minY;

  if (x >= 0 && x <= btnArea.maxX && y >= 0 && y <= btnArea.maxY) {
    circle.posX = x;
    circle.posY = y;
  } else {
    circle.posX = btnArea.maxX / 2;
    circle.posY = btnArea.maxY / 2;
  }

  circle.isVisible = true;
};

onMounted(() => {
  isMounted.value = true;
});
</script>

<style lang="scss">
@import './scss/BaseButton.scss';
</style>
